/**
 * PATH: apps/qa/src/lib/offline-db/operations.ts
 * PURPOSE: All IndexedDB CRUD operations for inspections, areas, evidence, sync queue, and metadata
 */

import { getDB } from './types'
import type { CachedInspection, CachedArea, CachedEvidence, SyncOperation } from './types'

// ── Inspection Operations ────────────────────────────────────────────────────

export async function cacheInspection(id: string, data: Record<string, unknown>, isOfflineCreated = false): Promise<void> {
  const db = await getDB()
  await db.put('inspections', { id, data, lastSynced: Date.now(), isOfflineCreated })
}

export async function getCachedInspection(id: string): Promise<CachedInspection | undefined> {
  return (await getDB()).get('inspections', id)
}

export async function getAllCachedInspections(): Promise<CachedInspection[]> {
  return (await getDB()).getAll('inspections')
}

export async function deleteCachedInspection(id: string): Promise<void> {
  await (await getDB()).delete('inspections', id)
}

// ── Area Operations ──────────────────────────────────────────────────────────

export async function cacheArea(id: string, inspectionId: string, data: Record<string, unknown>, items: unknown[]): Promise<void> {
  const db = await getDB()
  await db.put('areas', { id, inspectionId, data, items, lastSynced: Date.now() })
}

export async function getCachedArea(id: string): Promise<CachedArea | undefined> {
  return (await getDB()).get('areas', id)
}

export async function getAreasForInspection(inspectionId: string): Promise<CachedArea[]> {
  return (await getDB()).getAllFromIndex('areas', 'by-inspection', inspectionId)
}

export async function deleteCachedArea(id: string): Promise<void> {
  await (await getDB()).delete('areas', id)
}

// ── Evidence Operations ──────────────────────────────────────────────────────

export async function storeEvidence(
  areaId: string, blob: Blob, metadata: CachedEvidence['metadata'], itemInstanceId?: string,
): Promise<string> {
  const db = await getDB()
  const id = `evidence_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
  await db.put('evidence', { id, areaId, itemInstanceId, blob, metadata, status: 'pending', createdAt: Date.now() })
  return id
}

export async function getCachedEvidence(id: string): Promise<CachedEvidence | undefined> {
  return (await getDB()).get('evidence', id)
}

export async function getEvidenceForArea(areaId: string): Promise<CachedEvidence[]> {
  return (await getDB()).getAllFromIndex('evidence', 'by-area', areaId)
}

export async function getPendingEvidence(): Promise<CachedEvidence[]> {
  return (await getDB()).getAllFromIndex('evidence', 'by-status', 'pending')
}

export async function updateEvidenceStatus(id: string, status: CachedEvidence['status'], uploadedAssetId?: string): Promise<void> {
  const db = await getDB()
  const evidence = await db.get('evidence', id)
  if (evidence) {
    evidence.status = status
    if (uploadedAssetId) evidence.uploadedAssetId = uploadedAssetId
    await db.put('evidence', evidence)
  }
}

export async function deleteEvidence(id: string): Promise<void> {
  await (await getDB()).delete('evidence', id)
}

// ── Sync Queue Operations ────────────────────────────────────────────────────

export async function queueOperation(
  operationType: SyncOperation['operationType'], entityType: SyncOperation['entityType'],
  entityId: string, payload: unknown,
): Promise<string> {
  const db = await getDB()
  const id = `op_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
  // Lazy import avoids circular init with auth store during module load.
  const { useAuthStore } = await import('@quotech/auth')
  const auth = useAuthStore.getState()
  await db.put('syncQueue', {
    id,
    operationType,
    entityType,
    entityId,
    payload,
    createdAt: Date.now(),
    attempts: 0,
    status: 'pending',
    ownerUserId: auth.user?.id != null ? String(auth.user.id) : undefined,
    ownerOrganisationId: auth.organisationId != null ? String(auth.organisationId) : undefined,
  })
  return id
}

export async function getPendingOperations(): Promise<SyncOperation[]> {
  const { useAuthStore } = await import('@quotech/auth')
  const auth = useAuthStore.getState()
  const userId = auth.user?.id != null ? String(auth.user.id) : null
  const orgId = auth.organisationId != null ? String(auth.organisationId) : null
  const pending = await (await getDB()).getAllFromIndex('syncQueue', 'by-status', 'pending')
  return pending.filter((op) => {
    if (!op.ownerUserId || !op.ownerOrganisationId || !userId || !orgId) return false
    return op.ownerUserId === userId && op.ownerOrganisationId === orgId
  })
}

export async function getFailedOperations(): Promise<SyncOperation[]> {
  return (await getDB()).getAllFromIndex('syncQueue', 'by-status', 'failed')
}

export async function updateOperationStatus(id: string, status: SyncOperation['status'], error?: string): Promise<void> {
  const db = await getDB()
  const operation = await db.get('syncQueue', id)
  if (operation) {
    operation.status = status; operation.attempts += 1; operation.lastAttempt = Date.now()
    if (error) operation.error = error
    await db.put('syncQueue', operation)
  }
}

export async function deleteOperation(id: string): Promise<void> {
  await (await getDB()).delete('syncQueue', id)
}

export async function clearSyncedOperations(): Promise<void> {
  const db = await getDB()
  const synced = await db.getAllFromIndex('syncQueue', 'by-status', 'synced')
  const tx = db.transaction('syncQueue', 'readwrite')
  for (const op of synced) await tx.store.delete(op.id)
  await tx.done
}

// ── Metadata Operations ──────────────────────────────────────────────────────

export async function setMetadata(key: string, value: unknown): Promise<void> {
  await (await getDB()).put('metadata', { key, value })
}

export async function getMetadata<T = unknown>(key: string): Promise<T | undefined> {
  const result = await (await getDB()).get('metadata', key)
  return result?.value as T | undefined
}

export async function getLastSyncTime(): Promise<number | undefined> {
  return getMetadata<number>('lastSyncTime')
}

export async function setLastSyncTime(time: number): Promise<void> {
  return setMetadata('lastSyncTime', time)
}

// ── Utility Functions ────────────────────────────────────────────────────────

export async function clearAllData(): Promise<void> {
  const db = await getDB()
  const stores: Array<'inspections' | 'areas' | 'evidence' | 'syncQueue' | 'metadata'> = [
    'inspections', 'areas', 'evidence', 'syncQueue', 'metadata',
  ]
  for (const store of stores) await db.clear(store)
}

export async function getStorageStats(): Promise<{
  inspections: number; areas: number; evidence: number; pendingOperations: number; failedOperations: number
}> {
  const db = await getDB()
  const [inspections, areas, evidence, pending, failed] = await Promise.all([
    db.count('inspections'), db.count('areas'), db.count('evidence'),
    db.countFromIndex('syncQueue', 'by-status', 'pending'),
    db.countFromIndex('syncQueue', 'by-status', 'failed'),
  ])
  return { inspections, areas, evidence, pendingOperations: pending, failedOperations: failed }
}
